import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, CalendarCheck, Navigation, MapPin, Clock, ChevronDown,
  GraduationCap, Award, Stethoscope, BadgeCheck, CheckCircle2,
  Activity, Users, Droplets,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileActionBar } from "@/components/MobileActionBar";
import { Reveal } from "@/components/motion";
import { DOCTOR, whatsappLink, mapsDirectionsLink, mapsEmbedSrc, SITE_URL } from "@/config/doctorConfig";
import { scrollToSection } from "@/lib/scroll";

const consultList = [
  "Fever and infections",
  "Hypertension / high blood pressure",
  "Diabetes",
  "Thyroid disorders",
  "Migraine and headache",
  "Weakness and fatigue",
  "Cholesterol disorders",
  "Gastritis, indigestion and abdominal complaints",
  "Dengue, typhoid and malaria",
  "Respiratory infections",
  "General medical evaluation",
  "Preventive health check-ups",
];

const pageFaqs = [
  {
    q: "Who is a good General Physician in Bhopal?",
    a: "A good general physician is an MD (Medicine / Internal Medicine) doctor who listens carefully, evaluates thoroughly, and explains your condition and treatment clearly. Dr. Suyash Bhadoriya is an MBBS, MD (Internal Medicine), CCEBDM physician and Assistant Professor practising at Shree Multispeciality Hospital, Saket Nagar, Bhopal. Rather than relying on labels, look for qualifications, a systematic approach, and clear communication — and decide after your own consultation experience.",
  },
  {
    q: "When should I consult a General Physician?",
    a: "Consult a physician for fever lasting more than 2–3 days, persistent weakness or fatigue, uncontrolled blood pressure or blood sugar, thyroid symptoms, headaches that keep recurring, digestive complaints, or any symptom that worries you. You can also consult for a general health evaluation or preventive check-up even when you feel well.",
  },
  {
    q: "Does Dr. Suyash Bhadoriya treat diabetes and hypertension?",
    a: "Yes. Dr. Bhadoriya holds a Certificate Course in Evidence-Based Diabetes Management (CCEBDM) and routinely evaluates and manages diabetes and high blood pressure — including medicines review, complication screening and lifestyle guidance.",
  },
  {
    q: "Can I consult for fever and infections?",
    a: "Yes. Fever evaluation is one of the most common reasons patients visit. Fevers due to viral illness, dengue, malaria, typhoid and respiratory or urinary infections are assessed with appropriate examination and targeted tests before treatment is advised.",
  },
  {
    q: "Where is Dr. Suyash Bhadoriya's clinic in Bhopal?",
    a: "The clinic is at Shree Multispeciality Hospital, 271/2A, Saket Nagar, near AIIMS Gate No. 3, Habib Ganj, Bhopal, Madhya Pradesh 462024. Use the Get Directions button on this page for Google Maps navigation.",
  },
  {
    q: "What are the OPD timings?",
    a: "OPD consultations are held Monday to Friday from 4:00 PM to 7:30 PM, and on Sunday from 1:00 PM to 3:00 PM. The OPD remains closed on Saturday.",
  },
  {
    q: "How can I book an appointment?",
    a: "You can book through the appointment form on this website, call 9009147999 or the clinic at 0755-3100126, or send a WhatsApp message to 9009147999. You will receive confirmation of your timing.",
  },
];

const physicianSchema = {
  "@context": "https://schema.org",
  "@type": ["Physician", "MedicalBusiness"],
  name: "Dr. Suyash Bhadoriya",
  medicalSpecialty: "Internal Medicine",
  url: `${SITE_URL}/general-physician-bhopal`,
  image: `${SITE_URL}/assets/doctor-portrait.jpg`,
  telephone: "+91 9009147999",
  address: {
    "@type": "PostalAddress",
    streetAddress: "271/2A Saket Nagar, Near AIIMS Gate No.3, Bhopal",
    addressLocality: "Bhopal",
    addressRegion: "Madhya Pradesh",
    postalCode: "462024",
    addressCountry: "IN",
  },
  areaServed: ["Bhopal", "Saket Nagar", "Habib Ganj", "Madhya Pradesh"],
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "16:00", closes: "19:30" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "13:00", closes: "15:00" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: pageFaqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const inputCardCls = "bg-white rounded-2xl border border-slate-200/80 shadow-soft";

export default function GeneralPhysicianBhopal() {
  const [open, setOpen] = useState(0);

  return (
    <>
      <Helmet>
        <title>General Physician in Bhopal | Dr. Suyash Bhadoriya, MD Medicine</title>
        <meta
          name="description"
          content="Consult Dr. Suyash Bhadoriya, MD Medicine, General Physician and Internal Medicine Specialist in Bhopal for fever, infections, hypertension, diabetes, thyroid disorders, migraine and other medical conditions."
        />
        <link rel="canonical" href={`${SITE_URL}/general-physician-bhopal`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="General Physician in Bhopal | Dr. Suyash Bhadoriya, MD Medicine" />
        <meta property="og:description" content="Consult Dr. Suyash Bhadoriya, MD Medicine, General Physician and Internal Medicine Specialist in Bhopal at Shree Multispeciality Hospital, Saket Nagar." />
        <meta property="og:url" content={`${SITE_URL}/general-physician-bhopal`} />
        <script type="application/ld+json">{JSON.stringify(physicianSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Header />
      <main className="bg-background" data-testid="gp-bhopal-page">
        {/* Hero / intro */}
        <section className="bg-navy-900 grain relative pt-28 lg:pt-36 pb-16 lg:pb-20">
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-mono tracking-wide text-slate-400 uppercase mb-7">
                <Link to="/" className="hover:text-teal-300 transition-colors">Home</Link>
                <span aria-hidden="true">/</span>
                <span className="text-teal-300">General Physician in Bhopal</span>
              </nav>
            </Reveal>
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-400/10 px-4 py-1.5 mb-6">
                <BadgeCheck size={14} className="text-teal-300" />
                <span className="text-xs font-mono tracking-wider text-teal-200 uppercase">{DOCTOR.qualifications}</span>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h1 className="font-serif font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-balance" data-testid="gp-h1">
                General Physician in Bhopal<span className="sr-only"> – Dr. Suyash Bhadoriya</span>
              </h1>
              <p className="mt-3 font-serif text-teal-300 text-xl sm:text-2xl" data-testid="gp-subheading">
                Dr. Suyash Bhadoriya – MD (Internal Medicine)
              </p>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
                Dr. Suyash Bhadoriya is an MD Medicine doctor, General Physician and Internal
                Medicine Specialist in Bhopal providing evaluation and treatment for common as
                well as complex adult medical conditions. Patients consult him for everything
                from fevers and infections to long-term conditions like diabetes, hypertension
                and thyroid disorders.
              </p>
            </Reveal>
            <Reveal delay={3}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/#appointment" data-testid="gp-hero-book-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-navy-900 bg-white hover:bg-teal-50 shadow-lift transition-colors duration-200">
                  <CalendarCheck size={16} /> Book Appointment
                </Link>
                <a href={DOCTOR.mobileHref} data-testid="gp-hero-call-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white border border-white/25 hover:border-teal-300/60 hover:text-teal-200 transition-colors duration-200">
                  <Phone size={16} /> Call Now
                </a>
                <a href={mapsDirectionsLink()} target="_blank" rel="noopener noreferrer" data-testid="gp-hero-directions-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors duration-200">
                  <Navigation size={15} /> Get Directions
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Featured service card */}
        <section className="relative z-10 -mt-10 pb-4" data-testid="gp-service-card-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="bg-white rounded-3xl border border-slate-200/80 shadow-lift p-7 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="gp-service-card">
                <div className="w-14 h-14 rounded-2xl bg-navy-900 flex items-center justify-center shrink-0">
                  <Stethoscope size={26} className="text-teal-300" />
                </div>
                <div className="flex-1">
                  <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">
                    General Physician Consultation
                  </h2>
                  <p className="mt-2 text-base text-slate-600 leading-relaxed">
                    Consultation for common and complex adult medical conditions.
                  </p>
                  <p className="mt-2 text-xs font-mono uppercase tracking-wider text-teal-700">
                    With specialist depth in Internal Medicine (MD)
                  </p>
                </div>
                <Link
                  to="/#appointment"
                  data-testid="gp-service-card-book-btn"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white bg-navy-900 hover:bg-navy-700 transition-colors duration-200 shrink-0"
                >
                  <CalendarCheck size={16} /> Book Appointment
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Secondary services in priority order */}
        <section className="pt-2 pb-2" data-testid="gp-services-priority">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Activity, title: "Internal Medicine", sub: "Specialist medical evaluation and management for adults." },
                { icon: Users, title: "Adult Healthcare", sub: "Comprehensive, continuous medical care for adults." },
                { icon: Droplets, title: "Diabetes Care", sub: "CCEBDM-certified, evidence-based diabetes management." },
              ].map((s) => (
                <Reveal key={s.title} delay={1}>
                  <div
                    data-testid={`gp-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="h-full bg-white rounded-2xl border border-slate-200/80 shadow-soft p-5 hover:-translate-y-1 hover:shadow-lift hover:border-teal-500/30 transition-[transform,box-shadow,border-color] duration-300"
                  >
                    <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center mb-3.5">
                      <s.icon size={18} className="text-teal-700" />
                    </div>
                    <h3 className="font-serif font-semibold text-slate-900 text-lg leading-snug">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.sub}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Consultation scope */}
        <section className="py-14 sm:py-16 lg:py-20" data-testid="gp-consult-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">
                What You Can Consult For
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                As a General Physician in Bhopal, Dr. Bhadoriya evaluates both acute and chronic
                medical conditions in adults. Whether it is a new symptom that needs diagnosis or
                an ongoing condition that needs structured long-term care, patients can consult for:
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="gp-consult-list">
              {consultList.map((item, i) => (
                <Reveal key={item} delay={Math.min(i, 6) * 0.5}>
                  <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-200/80 shadow-soft px-5 py-3.5">
                    <CheckCircle2 size={17} className="text-teal-600 shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* About */}
        <section className="bg-white border-y border-slate-200/70 py-16 sm:py-20" data-testid="gp-about-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">
                About Dr. Suyash Bhadoriya
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <div className={`${inputCardCls} mt-8 p-6 sm:p-8`}>
                <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start">
                  <img
                    src={DOCTOR.photoHero}
                    alt="Dr. Suyash Bhadoriya — General Physician and Internal Medicine Doctor in Bhopal"
                    loading="lazy"
                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover shadow-frame shrink-0"
                  />
                  <dl className="space-y-3 text-sm sm:text-base">
                    <div className="flex gap-3"><GraduationCap size={18} className="text-teal-600 shrink-0 mt-0.5" /><p><dt className="inline font-semibold text-slate-900">Name: </dt><dd className="inline text-slate-600">Dr. Suyash Bhadoriya</dd></p></div>
                    <div className="flex gap-3"><Award size={18} className="text-teal-600 shrink-0 mt-0.5" /><p><dt className="inline font-semibold text-slate-900">Qualification: </dt><dd className="inline text-slate-600">MBBS, MD (Medicine / Internal Medicine)</dd></p></div>
                    <div className="flex gap-3"><Stethoscope size={18} className="text-teal-600 shrink-0 mt-0.5" /><p><dt className="inline font-semibold text-slate-900">Additional training: </dt><dd className="inline text-slate-600">CCEBDM (Diabetes)</dd></p></div>
                    <div className="flex gap-3"><BadgeCheck size={18} className="text-teal-600 shrink-0 mt-0.5" /><p><dt className="inline font-semibold text-slate-900">Practice: </dt><dd className="inline text-slate-600">General Physician &amp; Internal Medicine Specialist</dd></p></div>
                    <div className="flex gap-3"><MapPin size={18} className="text-teal-600 shrink-0 mt-0.5" /><p><dt className="inline font-semibold text-slate-900">Location: </dt><dd className="inline text-slate-600">Bhopal, Madhya Pradesh</dd></p></div>
                  </dl>
                </div>
                <p className="mt-6 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100 pt-5">
                  If you are searching for a dependable physician in Bhopal who combines specialist
                  training with an unhurried, patient-first consultation style, you can read more on
                  the <Link to="/#about" className="text-teal-700 font-semibold hover:text-teal-900 transition-colors">About page</Link> or
                  view his <Link to="/publications" className="text-teal-700 font-semibold hover:text-teal-900 transition-colors">research publications</Link>.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Why consult */}
        <section className="py-16 sm:py-20" data-testid="gp-why-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">
                Why Consult an Internal Medicine / General Physician?
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <div className="mt-6 space-y-4 text-base text-slate-600 leading-relaxed">
                <p>
                  An MD Medicine physician is trained to diagnose and manage the full range of adult
                  medical conditions — not just one organ system. That matters because most real
                  patients do not have one neat problem: diabetes often travels with high blood
                  pressure, thyroid disorders with fatigue, and fevers with other underlying
                  conditions.
                </p>
                <p>
                  An Internal Medicine doctor in Bhopal like Dr. Bhadoriya looks at the whole
                  picture — your symptoms, reports, medicines and lifestyle together — which leads to
                  more accurate diagnosis and fewer unnecessary referrals. For patients managing
                  several health problems at once, a physician in Bhopal who coordinates all of it
                  under one roof makes care simpler, safer and more consistent.
                </p>
                <p>
                  When a condition genuinely needs a super-specialist, your physician identifies that
                  early and guides you to the right one — while continuing to manage the rest of your
                  health. For patients managing several health problems at once, having one trusted
                  Physician in Bhopal who coordinates all of it makes care simpler, safer and more
                  consistent.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Clinic info */}
        <section className="bg-white border-y border-slate-200/70 py-16 sm:py-20" data-testid="gp-clinic-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">
                Clinic Information
              </h2>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Reveal delay={1}>
                <address className={`${inputCardCls} not-italic p-6 sm:p-8 h-full`}>
                  <p className="flex gap-3"><MapPin size={18} className="text-teal-600 shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-slate-700 leading-relaxed">
                      <strong className="text-slate-900">{DOCTOR.hospitalName}</strong><br />
                      271/2A, Saket Nagar,<br />
                      Near AIIMS Gate No. 3,<br />
                      Bhopal, Madhya Pradesh – 462024
                    </span>
                  </p>
                  <p className="mt-5 flex gap-3"><Phone size={18} className="text-teal-600 shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-slate-700">
                      <a href={DOCTOR.mobileHref} className="font-semibold text-navy-900 hover:text-teal-700 transition-colors">9009147999</a><br />
                      <a href={DOCTOR.clinicPhoneHref} className="font-semibold text-navy-900 hover:text-teal-700 transition-colors">0755-3100126</a>
                    </span>
                  </p>
                  <p className="mt-5 flex gap-3"><Clock size={18} className="text-teal-600 shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-slate-700">
                      <strong className="text-slate-900">OPD:</strong><br />
                      Monday–Friday: 4:00 PM–7:30 PM<br />
                      Saturday: Closed<br />
                      Sunday: 1:00 PM–3:00 PM
                    </span>
                  </p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link to="/#appointment" data-testid="gp-clinic-book-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-navy-900 hover:bg-navy-700 transition-colors duration-200">
                      <CalendarCheck size={15} /> Book Appointment
                    </Link>
                    <a href={DOCTOR.mobileHref} data-testid="gp-clinic-call-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-navy-900 border border-slate-300 hover:border-teal-600 hover:text-teal-700 transition-colors duration-200">
                      <Phone size={15} /> Call Now
                    </a>
                    <a href={mapsDirectionsLink()} target="_blank" rel="noopener noreferrer" data-testid="gp-clinic-directions-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors duration-200">
                      <Navigation size={15} /> Get Directions
                    </a>
                    <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="gp-clinic-whatsapp-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-[#128C7E] hover:bg-[#0d6e63] transition-colors duration-200">
                      <WhatsAppIcon size={15} /> WhatsApp
                    </a>
                  </div>
                </address>
              </Reveal>
              <Reveal delay={2}>
                <div className="h-full min-h-[320px] rounded-2xl overflow-hidden border border-slate-200/80 shadow-soft">
                  <iframe
                    title="Map — General Physician in Saket Nagar, Bhopal"
                    src={mapsEmbedSrc()}
                    className="w-full h-full min-h-[320px] border-0"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20" data-testid="gp-faq-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">
                Frequently Asked Questions
              </h2>
            </Reveal>
            <div className="mt-8 space-y-3" data-testid="gp-faq-list">
              {pageFaqs.map((f, i) => {
                const isOpen = open === i;
                return (
                  <Reveal key={f.q} delay={Math.min(i, 4) * 0.5}>
                    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-soft overflow-hidden">
                      <button
                        onClick={() => setOpen(isOpen ? -1 : i)}
                        aria-expanded={isOpen}
                        data-testid={`gp-faq-question-${i}`}
                        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                      >
                        <span className="font-semibold text-slate-900 text-sm sm:text-base">{f.q}</span>
                        <ChevronDown size={18} className={`shrink-0 text-teal-700 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          >
                            <p className="px-6 pb-6 text-sm sm:text-base text-slate-600 leading-relaxed" data-testid={`gp-faq-answer-${i}`}>{f.a}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="rounded-3xl bg-navy-900 grain relative overflow-hidden p-8 sm:p-12 text-center">
                <div className="relative">
                  <h2 className="font-serif font-bold text-white text-2xl sm:text-3xl">Consult a Physician in Bhopal Today</h2>
                  <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-xl mx-auto leading-relaxed">
                    Book a consultation with Dr. Suyash Bhadoriya at Shree Multispeciality
                    Hospital, Saket Nagar — near AIIMS Bhopal.
                  </p>
                  <div className="mt-7 flex flex-wrap justify-center gap-3">
                    <Link to="/#appointment" data-testid="gp-cta-book-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-navy-900 bg-white hover:bg-teal-50 transition-colors">
                      <CalendarCheck size={16} /> Book Appointment
                    </Link>
                    <a href={DOCTOR.mobileHref} data-testid="gp-cta-call-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white border border-white/25 hover:border-teal-300/60 transition-colors">
                      <Phone size={16} /> Call Now
                    </a>
                    <a href={mapsDirectionsLink()} target="_blank" rel="noopener noreferrer" data-testid="gp-cta-directions-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors">
                      <Navigation size={15} /> Get Directions
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
}
